"use client";

import { cn } from "@/lib/utils";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import { Fragment, useMemo } from "react";

/**
 * Accreditation 1052 — KUK home accreditation strip.
 *
 * Port of kuk-campus-portal `components/Home/Accreditations.tsx`: a short tinted
 * band directly under the hero carrying each accreditation badge over its label,
 * with a hairline rule between neighbours.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px and `lg:` is 1024px. This app remaps `md:` to 1230px and exposes
 * 768px as `tab:` (see globals.css `--breakpoint-*`), so the reference's `md:`
 * is ported as `tab:` and its `lg:` as this app's `sm:` (1080px).
 *
 * COLUMN WIDTHS — v1 sets `w-1/3` on every card and then overrides it to `w-1/2`
 * for two badges and `w-1/4` for four; five or more fall back to the `w-1/3`
 * base and scroll. Reproduced exactly, including that fallback.
 *
 * THE BAND TINT — v1 writes `bg-primary-900 bg-opacity-20`, i.e. the brand
 * maroon at 20%. That resolves to `rgba(159, 28, 51, 0.2)`, which is the default
 * here; it is a single CMS colour, so an editor can set any RGBA directly.
 *
 * DROPPED — v1 renders the label TWICE, once `md:hidden` and once `hidden
 * md:block`, with identical classes and identical text. Both copies are in the
 * served HTML and exactly one is ever visible, so this renders it once.
 */

interface BadgeRow {
  id?: string | number;
  meta_image?: string;
  meta_alt_image?: string;
  meta_title?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface Accreditation1052Data {
  metadata?: BadgeRow[];
  /** The band behind the row. */
  background_color?: string;
  /** Badge labels. */
  title_text_color?: string;
  /** The hairline between neighbours. */
  divider_color?: string;
  status?: boolean;
}

/** v1 narrows the card to the badge count for two, three and four badges. */
const WIDTH_BY_COUNT: Record<number, string> = {
  2: "w-1/2",
  3: "w-1/3",
  4: "w-1/4",
};

const Accreditation1052 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as Accreditation1052Data | undefined;

  const {
    background_color = "rgba(159, 28, 51, 0.2)",
    title_text_color = "#031F44",
    divider_color = "#9F1C3345",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);

  const badges = useMemo(() => {
    const rows = Array.isArray(sectionData?.metadata) ? sectionData!.metadata! : [];
    return rows
      .filter((r) => r?.meta_status !== false)
      .filter((r) => !!(r?.meta_image || r?.meta_title))
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  if (!status || badges.length === 0) return null;

  const cardWidth = WIDTH_BY_COUNT[badges.length] || "w-1/3";

  return (
    <section
      className="flex h-[120px] w-full items-center justify-center"
      style={{ backgroundColor: background_color }}
    >
      <div className="mx-auto w-full">
        <div
          className={cn(
            "flex w-full items-center overflow-x-auto [scrollbar-width:none]",
            "[&::-webkit-scrollbar]:hidden",
            "tab:justify-between tab:space-x-8 tab:px-56",
          )}
        >
          {badges.map((badge, i) => (
            <Fragment key={badge.id ?? i}>
              <div
                className={cn(
                  "flex flex-col items-center justify-center tab:min-w-0",
                  cardWidth,
                )}
              >
                {badge.meta_image && (
                  <Image
                    src={badge.meta_image}
                    alt={badge.meta_alt_image || badge.meta_title || "Accreditation"}
                    width={48}
                    height={48}
                    loading="lazy"
                    className="size-[35px] tab:size-[48px]"
                  />
                )}
                {badge.meta_title && (
                  <p
                    className="mt-4 text-center text-sm font-medium sm:text-lg"
                    style={{ color: title_text_color }}
                  >
                    {badge.meta_title}
                  </p>
                )}
              </div>
              {i !== badges.length - 1 && (
                <div
                  aria-hidden
                  className="mx-4 h-[100px] shrink-0 border-r"
                  style={{ borderColor: divider_color }}
                />
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Accreditation1052;
